/**
 * 共通通知サービス
 * 後で MatSnackBar 等に差し替えしやすい形
 */

import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { LoadingNotificationSnackbarComponent } from './loading-notification-snackbar.component';
import { UndoableNotificationSnackbarComponent } from './undoable-notification-snackbar.component';

/** 通知レベル */
export type NotificationLevel = 'success' | 'error' | 'info';
export type UndoableNotificationResult = 'confirmed' | 'cancelled';

export interface LoadingNotificationHandle {
  dismiss(): Promise<void>;
}

export interface UndoableNotificationOptions {
  cancelLabel?: string;
  closeLabel?: string;
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private static readonly debugLoadingHoldMsStorageKey = 'prosche_debug_loading_snackbar_hold_ms';

  constructor(private readonly snackBar: MatSnackBar) {}

  showLoading(message: string): LoadingNotificationHandle {
    const snackBarRef = this.snackBar.openFromComponent(LoadingNotificationSnackbarComponent, {
      announcementMessage: message,
      horizontalPosition: 'center',
      politeness: 'polite',
      verticalPosition: 'bottom',
      panelClass: ['snackbar-loading'],
      data: { message },
    });
    const dismissed = firstValueFrom(snackBarRef.afterDismissed());
    let dismissRequested = false;

    return {
      dismiss: async () => {
        if (!dismissRequested) {
          dismissRequested = true;
          snackBarRef.dismiss();
        }

        await dismissed;
      },
    };
  }

  async runWithLoading<T>(message: string, action: () => Promise<T>): Promise<T> {
    const loadingNotification = this.showLoading(message);

    try {
      return await action();
    } finally {
      const debugHoldMs = this.getDebugLoadingHoldMs();
      if (debugHoldMs > 0) {
        await new Promise<void>(resolve => setTimeout(resolve, debugHoldMs));
      }

      await loadingNotification.dismiss();
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
  }

  private getDebugLoadingHoldMs(): number {
    try {
      const rawValue = globalThis.localStorage?.getItem(NotificationService.debugLoadingHoldMsStorageKey);
      if (!rawValue) {
        return 0;
      }

      const parsedValue = Number(rawValue);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
    } catch {
      return 0;
    }
  }

  showSuccess(message: string): void {
    this.show(message, 'success');
  }

  showError(message: string): void {
    this.show(message, 'error');
  }

  showInfo(message: string): void {
    this.show(message, 'info');
  }

  showUndoableInfo(
    message: string,
    options: UndoableNotificationOptions = {},
  ): Promise<UndoableNotificationResult> {
    const snackBarRef = this.snackBar.openFromComponent(UndoableNotificationSnackbarComponent, {
      duration: options.duration ?? 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['snackbar-undoable'],
      data: {
        message,
        cancelLabel: options.cancelLabel ?? '取消',
        closeLabel: options.closeLabel ?? '閉じる',
      },
    });

    return new Promise<UndoableNotificationResult>(resolve => {
      let settled = false;
      const settle = (result: UndoableNotificationResult): void => {
        if (settled) {
          return;
        }

        settled = true;
        cancelSub.unsubscribe();
        closeSub.unsubscribe();
        dismissSub.unsubscribe();
        resolve(result);
      };

      const cancelSub = snackBarRef.instance.cancelRequested.subscribe(() => {
        snackBarRef.dismiss();
        settle('cancelled');
      });

      const closeSub = snackBarRef.instance.closeRequested.subscribe(() => {
        snackBarRef.dismiss();
        settle('confirmed');
      });

      const dismissSub = snackBarRef.afterDismissed().subscribe(() => {
        settle('confirmed');
      });
    });
  }

  private show(message: string, level: NotificationLevel): void {
    const duration = level === 'error' ? 5000 : 3000;
    const panelClass = level === 'success'
      ? ['snackbar-success']
      : level === 'error'
        ? ['snackbar-error']
        : [];

    this.snackBar.open(message, '閉じる', { duration, panelClass });
  }
}
